import { useState, useCallback, useRef } from 'react'
import {
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  ArrowUpRight,
  Minus,
  Plus,
  Pencil,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useImportRuns,
  useImportRun,
  useUploadBulkSheet,
  useApplyImport,
  useExportXlsx,
  type ImportRun,
} from '@/api/bulk-import'

// ============================================================
//  Status Badge
// ============================================================

function ImportStatusBadge({ status }: { status: string }) {
  const config: Record<string, { icon: typeof CheckCircle2; className: string; label: string }> = {
    uploaded: { icon: Clock, className: 'bg-blue-100 text-blue-700', label: 'Hochgeladen' },
    diffed: { icon: AlertCircle, className: 'bg-yellow-100 text-yellow-700', label: 'Diff bereit' },
    applied: { icon: CheckCircle2, className: 'bg-green-100 text-green-700', label: 'Angewendet' },
    failed: { icon: XCircle, className: 'bg-red-100 text-red-700', label: 'Fehlgeschlagen' },
  }
  const fallback = { icon: Clock, className: 'bg-blue-100 text-blue-700', label: 'Hochgeladen' }
  const c = config[status] ?? fallback
  const Icon = c.icon
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', c.className)}>
      <Icon className="h-3 w-3" />
      {c.label}
    </span>
  )
}

// ============================================================
//  Diff Table
// ============================================================

function DiffTable({ diffData }: { diffData: ImportRun['diffData'] }) {
  const [tab, setTab] = useState<'added' | 'modified' | 'removed'>('added')

  if (!diffData) return null

  const tabs = [
    { key: 'added' as const, label: 'Hinzugefuegt', count: diffData.added.length, icon: Plus, color: 'text-green-600' },
    { key: 'modified' as const, label: 'Geaendert', count: diffData.modified.length, icon: Pencil, color: 'text-yellow-600' },
    { key: 'removed' as const, label: 'Entfernt', count: diffData.removed.length, icon: Minus, color: 'text-red-600' },
  ]

  const entries = diffData[tab]

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              tab === t.key
                ? 'bg-[var(--sidebar-accent)] text-[var(--sidebar-accent-foreground)]'
                : 'text-[var(--muted-foreground)] hover:bg-[var(--sidebar-accent)]',
            )}
          >
            <t.icon className={cn('h-3.5 w-3.5', t.color)} />
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-[var(--muted-foreground)] py-4">Keine Eintraege in dieser Kategorie.</p>
      ) : (
        <div className="overflow-auto max-h-[400px] border border-[var(--border)] rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-[var(--muted)] sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Typ</th>
                <th className="text-left px-3 py-2 font-medium">Amazon ID</th>
                <th className="text-left px-3 py-2 font-medium">Name</th>
                {tab === 'modified' && <th className="text-left px-3 py-2 font-medium">Aenderungen</th>}
              </tr>
            </thead>
            <tbody>
              {entries.slice(0, 100).map((entry, i) => (
                <tr key={`${entry.amazonId}-${i}`} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2">
                    <span className="inline-block rounded bg-[var(--muted)] px-1.5 py-0.5 text-xs">
                      {entry.entityType}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{entry.amazonId}</td>
                  <td className="px-3 py-2">{entry.name || '—'}</td>
                  {tab === 'modified' && (
                    <td className="px-3 py-2 text-xs">
                      {entry.changes && Object.entries(entry.changes).map(([field, vals]) => (
                        <div key={field} className="flex gap-1">
                          <span className="font-medium">{field}:</span>
                          <span className="text-red-500 line-through">{String(vals.old)}</span>
                          <span>→</span>
                          <span className="text-green-600">{String(vals.new)}</span>
                        </div>
                      ))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {entries.length > 100 && (
            <p className="text-xs text-[var(--muted-foreground)] px-3 py-2">
              ... und {entries.length - 100} weitere Eintraege
            </p>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================
//  Main Page
// ============================================================

export default function BulkImport() {
  const [selectedImportId, setSelectedImportId] = useState<number | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: runsData, isPending: runsLoading } = useImportRuns()
  const { data: selectedData } = useImportRun(selectedImportId)
  const uploadMutation = useUploadBulkSheet()
  const applyMutation = useApplyImport()
  const exportMutation = useExportXlsx()

  const selectedImport = selectedData?.import ?? null

  const handleFile = useCallback(
    (file: File) => {
      uploadMutation.mutate(file, {
        onSuccess: (data) => {
          setSelectedImportId(data.import.id)
        },
      })
    },
    [uploadMutation],
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      const file = e.dataTransfer.files[0]
      if (file) handleFile(file)
    },
    [handleFile],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false)
  }, [])

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) handleFile(file)
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [handleFile],
  )

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
    } catch {
      return dateStr
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bulk Import</h1>
          <p className="text-sm text-[var(--muted-foreground)]">
            Amazon Bulk-Sheet hochladen, Aenderungen pruefen und anwenden
          </p>
        </div>
        <button
          onClick={() => exportMutation.mutate()}
          disabled={exportMutation.isPending}
          className="flex items-center gap-2 rounded-md bg-[var(--sidebar-accent)] px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {exportMutation.isPending ? 'Exportiere...' : 'DB exportieren'}
        </button>
      </div>

      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors',
          isDragOver
            ? 'border-blue-500 bg-blue-50'
            : 'border-[var(--border)] hover:border-blue-400 hover:bg-[var(--muted)]',
          uploadMutation.isPending && 'pointer-events-none opacity-50',
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileInput}
          className="hidden"
        />
        {uploadMutation.isPending ? (
          <>
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
            <p className="mt-3 text-sm font-medium">Verarbeite Upload...</p>
          </>
        ) : (
          <>
            <Upload className="h-8 w-8 text-[var(--muted-foreground)]" />
            <p className="mt-3 text-sm font-medium">XLSX-Datei hierher ziehen oder klicken</p>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              Unterstuetzt: Amazon Bulk-Sheet Export (.xlsx)
            </p>
          </>
        )}
      </div>

      {uploadMutation.isError && (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          <XCircle className="h-4 w-4" />
          Upload fehlgeschlagen: {uploadMutation.error?.message}
        </div>
      )}

      {/* Selected Import Detail */}
      {selectedImport && (
        <div className="rounded-lg border border-[var(--border)] p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm font-medium">{selectedImport.filename}</p>
                <p className="text-xs text-[var(--muted-foreground)]">
                  {formatDate(selectedImport.createdAt)}
                </p>
              </div>
              <ImportStatusBadge status={selectedImport.status} />
            </div>
            {selectedImport.status === 'diffed' && (
              <button
                onClick={() => applyMutation.mutate(selectedImport.id)}
                disabled={applyMutation.isPending}
                className="flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                <ArrowUpRight className="h-4 w-4" />
                {applyMutation.isPending ? 'Wende an...' : 'Aenderungen anwenden'}
              </button>
            )}
          </div>

          {/* Entity Counts */}
          {selectedImport.entityCounts && (
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'Campaigns', count: selectedImport.entityCounts.campaigns },
                { label: 'Ad Groups', count: selectedImport.entityCounts.adGroups },
                { label: 'Keywords', count: selectedImport.entityCounts.keywords },
                { label: 'Negative KW', count: selectedImport.entityCounts.negativeKeywords },
              ].map(item => (
                <div key={item.label} className="rounded-md bg-[var(--muted)] px-3 py-2 text-center">
                  <p className="text-lg font-semibold">{item.count.toLocaleString('de-DE')}</p>
                  <p className="text-xs text-[var(--muted-foreground)]">{item.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Diff Summary */}
          {selectedImport.diffData && (
            <div className="space-y-2">
              <div className="flex gap-4 text-sm">
                <span className="flex items-center gap-1 text-green-600">
                  <Plus className="h-3.5 w-3.5" /> {selectedImport.diffData.added.length} neu
                </span>
                <span className="flex items-center gap-1 text-yellow-600">
                  <Pencil className="h-3.5 w-3.5" /> {selectedImport.diffData.modified.length} geaendert
                </span>
                <span className="flex items-center gap-1 text-red-600">
                  <Minus className="h-3.5 w-3.5" /> {selectedImport.diffData.removed.length} entfernt
                </span>
              </div>
              <DiffTable diffData={selectedImport.diffData} />
            </div>
          )}

          {selectedImport.errorMessage && (
            <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
              {selectedImport.errorMessage}
            </div>
          )}
        </div>
      )}

      {/* Import History */}
      <div>
        <h2 className="text-lg font-semibold mb-3">Import-Verlauf</h2>
        {runsLoading ? (
          <p className="text-sm text-[var(--muted-foreground)]">Lade...</p>
        ) : !runsData?.runs.length ? (
          <p className="text-sm text-[var(--muted-foreground)]">Noch keine Imports vorhanden.</p>
        ) : (
          <div className="overflow-auto border border-[var(--border)] rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-[var(--muted)]">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Datei</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Entities</th>
                  <th className="text-left px-3 py-2 font-medium">Diff</th>
                  <th className="text-left px-3 py-2 font-medium">Datum</th>
                  <th className="text-left px-3 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {runsData.runs.map(run => (
                  <tr
                    key={run.id}
                    className={cn(
                      'border-t border-[var(--border)] cursor-pointer hover:bg-[var(--muted)]',
                      selectedImportId === run.id && 'bg-[var(--sidebar-accent)]',
                    )}
                    onClick={() => setSelectedImportId(run.id)}
                  >
                    <td className="px-3 py-2 flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4 text-green-600" />
                      {run.filename}
                    </td>
                    <td className="px-3 py-2">
                      <ImportStatusBadge status={run.status} />
                    </td>
                    <td className="px-3 py-2 text-xs text-[var(--muted-foreground)]">
                      {run.entityCounts
                        ? `${run.entityCounts.campaigns} C / ${run.entityCounts.adGroups} AG / ${run.entityCounts.keywords} KW`
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {run.diffData ? (
                        <span className="flex gap-2">
                          <span className="text-green-600">+{run.diffData.added.length}</span>
                          <span className="text-yellow-600">~{run.diffData.modified.length}</span>
                          <span className="text-red-600">-{run.diffData.removed.length}</span>
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-[var(--muted-foreground)]">
                      {formatDate(run.createdAt)}
                    </td>
                    <td className="px-3 py-2">
                      <button className="text-xs text-blue-600 hover:underline">Details</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
